import { useCallback, useEffect, useState } from "react";
import { MapPin, CreditCard, Ban, PlayCircle, XOctagon, Loader2, ShieldCheck, Repeat, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { AdminProposeTerritoryModal } from "@/components/AdminProposeTerritoryModal";

const ACTIVATION_LABELS = {
  VALIDE: "Validé — territoire en attente de choix",
  TERRITOIRE_CHOISI: "Territoire choisi",
  PAIEMENT_EN_ATTENTE: "Souscription en attente",
  DOCUMENTS_EN_ATTENTE: "Documents en attente",
  ESPACE_ACTIF_TERRITOIRE_EN_ATTENTE: "Espace Partenaire actif — territoire en attente d'activation",
  ACTIF: "Actif",
  SUSPENDU: "Suspendu",
  RESILIE: "Résilié",
};

const ACTIVATION_CLS = {
  VALIDE: "bg-sky-100 text-sky-700", TERRITOIRE_CHOISI: "bg-indigo-100 text-indigo-700",
  PAIEMENT_EN_ATTENTE: "bg-amber-100 text-amber-700", DOCUMENTS_EN_ATTENTE: "bg-orange-100 text-orange-700",
  ESPACE_ACTIF_TERRITOIRE_EN_ATTENTE: "bg-indigo-100 text-indigo-700",
  ACTIF: "bg-sage/20 text-navy", SUSPENDU: "bg-amber-100 text-amber-700", RESILIE: "bg-red-100 text-red-600",
};

export const AdminPartnerTerritory = ({ partner, onUpdated }) => {
  const [allTerritories, setAllTerritories] = useState([]);
  const [selectedNeighbors, setSelectedNeighbors] = useState(partner.allowed_neighbor_ids || []);
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState("");
  const [showReasonFor, setShowReasonFor] = useState(null);
  const [proposeOpen, setProposeOpen] = useState(false);

  const fetchTerritories = useCallback(() => {
    api.get("/admin/territories").then((res) => setAllTerritories(res.data)).catch(() => {});
  }, []);

  useEffect(() => { fetchTerritories(); }, [fetchTerritories]);
  useEffect(() => { setSelectedNeighbors(partner.allowed_neighbor_ids || []); }, [partner.id, partner.allowed_neighbor_ids]);

  const proposed = allTerritories.find((t) => t.id === partner.proposed_territory_id);
  const orphanedTerritories = allTerritories.filter(
    (t) => t.partner_id === partner.id && t.status === "attribue" && !(partner.territory_ids || []).includes(t.id)
  );
  const neighborOptions = proposed
    ? allTerritories.filter((t) => t.id !== proposed.id && (proposed.neighbor_ids || []).includes(t.id))
    : [];

  const toggleNeighbor = (id) =>
    setSelectedNeighbors((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const saveNeighbors = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/partners/${partner.id}/neighbor-territories`, { territory_ids: selectedNeighbors });
      toast.success("Territoires complémentaires mis à jour");
      onUpdated();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const runAction = async (action) => {
    setBusy(true);
    try {
      const res = await api.post(`/admin/partners/${partner.id}/action`, { action });
      toast.success(`Statut partenaire : ${ACTIVATION_LABELS[res.data.activation_status] || res.data.activation_status}`);
      onUpdated();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const changeSubscriptionType = async (subscription_type) => {
    setBusy(true);
    try {
      await api.patch(`/admin/partners/${partner.id}/subscription`, { subscription_type, reason: reason || undefined });
      toast.success(subscription_type === "EXONERE" ? "Partenaire exonéré d'abonnement" : "Abonnement standard rétabli");
      setShowReasonFor(null);
      setReason("");
      onUpdated();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-partner-territory" className="mt-7">
      <div className="flex items-center justify-between">
        <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Territoire & activation</p>
        <span data-testid="admin-partner-activation-status" className={`rounded-full px-3 py-1 text-xs font-bold ${ACTIVATION_CLS[partner.activation_status]}`}>
          {ACTIVATION_LABELS[partner.activation_status]}
        </span>
      </div>

      {proposed && (
        <div className="mt-3 flex items-center gap-2 rounded-xl border border-slate-200 p-3.5">
          <MapPin className="h-4 w-4 shrink-0 text-cognac" />
          <div>
            <p className="text-sm font-bold text-navy">{proposed.name}</p>
            <p className="text-xs text-slate-400">{proposed.code} · {proposed.department}</p>
          </div>
        </div>
      )}

      {orphanedTerritories.length > 0 && (
        <div className="mt-3 rounded-xl border border-amber-300 bg-amber-50 p-3.5" data-testid="admin-territory-mismatch-warning">
          <p className="text-sm font-bold text-amber-800">Incohérence détectée</p>
          <p className="mt-1 text-xs text-amber-700">
            {orphanedTerritories.map((t) => `${t.name} (${t.code})`).join(", ")} {orphanedTerritories.length > 1 ? "sont attribués" : "est attribué"} à ce partenaire côté territoires, mais absent de sa liste `territory_ids` (donc invisible dans son Espace Partenaire).
          </p>
          <button
            type="button" data-testid="admin-resync-territories-btn" disabled={busy}
            onClick={() => runAction("resynchroniser_territoires")}
            className="mt-2 flex items-center gap-1.5 rounded-full bg-amber-600 px-4 py-1.5 text-xs font-bold text-white transition-colors hover:bg-amber-700 disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Resynchroniser mes territoires
          </button>
        </div>
      )}

      {partner.activation_status === "VALIDE" && (
        <button
          type="button" data-testid="admin-propose-territory-btn" onClick={() => setProposeOpen(true)}
          className="mt-3 flex items-center gap-1.5 rounded-full border border-cognac/40 px-4 py-1.5 text-xs font-bold text-cognac transition-colors hover:bg-cognac/10"
        >
          <Repeat className="h-3.5 w-3.5" /> {proposed ? "Changer le territoire proposé" : "Proposer un territoire"}
        </button>
      )}

      {proposeOpen && (
        <AdminProposeTerritoryModal
          partner={partner}
          territories={allTerritories}
          currentProposed={proposed}
          onClose={() => setProposeOpen(false)}
          onProposed={onUpdated}
        />
      )}

      {partner.activation_status === "VALIDE" && neighborOptions.length > 0 && (
        <div className="mt-3">
          <p className="text-xs font-semibold text-slate-500">Territoires complémentaires autorisés (limitrophes) :</p>
          <div className="mt-2 space-y-2">
            {neighborOptions.map((t) => (
              <label key={t.id} data-testid={`admin-neighbor-option-${t.code}`} className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-xs">
                <input type="checkbox" checked={selectedNeighbors.includes(t.id)} onChange={() => toggleNeighbor(t.id)} className="h-4 w-4 accent-cognac" />
                {t.name} <span className="text-slate-400">({t.status})</span>
              </label>
            ))}
          </div>
          <button
            type="button"
            data-testid="admin-save-neighbors"
            onClick={saveNeighbors}
            disabled={busy}
            className="mt-2 rounded-full bg-navy px-4 py-1.5 text-xs font-bold text-white transition-colors hover:bg-cognac disabled:opacity-50"
          >
            Enregistrer les territoires complémentaires
          </button>
        </div>
      )}

      {partner.territory_ids?.length > 0 && (
        <p className="mt-3 text-sm text-slate-500">
          Tarif calculé : <span className="font-bold text-navy">{partner.monthly_price_ht} € HT / mois</span> pour {partner.territory_ids.length} territoire(s)
        </p>
      )}

      <div className="mt-4 rounded-xl border border-slate-200 p-3.5" data-testid="admin-partner-subscription">
        <div className="flex items-center justify-between">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Type d'abonnement</p>
          <span
            data-testid="admin-partner-subscription-badge"
            className={`rounded-full px-3 py-1 text-xs font-bold ${partner.subscription_type === "EXONERE" ? "bg-sage/20 text-navy" : "bg-slate-100 text-slate-600"}`}
          >
            {partner.subscription_type === "EXONERE" ? "EXONÉRÉ" : "STANDARD"}
          </span>
        </div>

        {partner.subscription_type === "EXONERE" && partner.exemption_started_at && (
          <p className="mt-2 text-xs text-slate-500">
            Exonéré depuis le {new Date(partner.exemption_started_at).toLocaleDateString("fr-FR")}
            {partner.exemption_reason ? ` — motif : ${partner.exemption_reason}` : ""}
          </p>
        )}

        <div className="mt-3 flex flex-wrap gap-2">
          <button
            type="button"
            data-testid="admin-set-subscription-standard"
            onClick={() => changeSubscriptionType("STANDARD")}
            disabled={busy || partner.subscription_type === "STANDARD"}
            className="rounded-full border border-slate-200 px-4 py-1.5 text-xs font-bold text-slate-600 transition-colors hover:bg-slate-50 disabled:opacity-40"
          >
            Standard — 249 € HT/mois
          </button>
          {showReasonFor === "EXONERE" ? (
            <div className="flex w-full flex-wrap items-center gap-2">
              <input
                type="text"
                data-testid="admin-exemption-reason-input"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Motif interne (facultatif)"
                className="min-w-[220px] flex-1 rounded-full border border-slate-200 px-4 py-1.5 text-xs"
              />
              <button
                type="button"
                data-testid="admin-confirm-exemption"
                onClick={() => changeSubscriptionType("EXONERE")}
                disabled={busy}
                className="flex items-center gap-1.5 rounded-full bg-sage/20 px-4 py-1.5 text-xs font-bold text-navy transition-colors hover:bg-sage/30 disabled:opacity-50"
              >
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5" />}
                Confirmer l'exonération
              </button>
              <button type="button" onClick={() => { setShowReasonFor(null); setReason(""); }} className="rounded-full border border-slate-200 px-3 py-1.5 text-xs text-slate-500">
                Annuler
              </button>
            </div>
          ) : (
            <button
              type="button"
              data-testid="admin-set-subscription-exonere"
              onClick={() => setShowReasonFor("EXONERE")}
              disabled={busy || partner.subscription_type === "EXONERE"}
              className="flex items-center gap-1.5 rounded-full border border-sage px-4 py-1.5 text-xs font-bold text-navy transition-colors hover:bg-sage/10 disabled:opacity-40"
            >
              <ShieldCheck className="h-3.5 w-3.5" /> Exonéré — 0 €
            </button>
          )}
        </div>
        <p className="mt-2 text-[11px] text-slate-400">Réservé à la Centrale. Le partenaire ne peut jamais modifier lui-même ce statut.</p>

        {(partner.history || []).filter((h) => h.action === "subscription_type_changed").length > 0 && (
          <div className="mt-3 border-t border-slate-100 pt-3">
            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Historique de l'abonnement</p>
            <ul className="mt-1.5 space-y-1" data-testid="admin-subscription-history">
              {partner.history.filter((h) => h.action === "subscription_type_changed").slice().reverse().map((h, i) => (
                <li key={i} className="text-[11px] text-slate-500">
                  {new Date(h.at).toLocaleDateString("fr-FR")} — {h.from} → <span className="font-semibold text-navy">{h.to}</span>
                  {h.reason ? ` (${h.reason})` : ""} par {h.by || "—"}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {["PAIEMENT_EN_ATTENTE", "DOCUMENTS_EN_ATTENTE"].includes(partner.activation_status) && (
          <button
            type="button"
            data-testid="admin-confirm-payment"
            onClick={() => runAction("confirmer_paiement")}
            disabled={busy}
            className="flex items-center gap-1.5 rounded-full bg-sage/20 px-4 py-2 text-xs font-bold text-navy transition-colors hover:bg-sage/30 disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CreditCard className="h-3.5 w-3.5" />}
            Confirmer la souscription (paiement simulé)
          </button>
        )}
        {partner.activation_status === "ESPACE_ACTIF_TERRITOIRE_EN_ATTENTE" && (
          <button
            type="button"
            data-testid="admin-activate-territory"
            onClick={() => runAction("activer_territoire")}
            disabled={busy}
            className="flex items-center gap-1.5 rounded-full bg-navy px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-cognac disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <PlayCircle className="h-3.5 w-3.5" />}
            Activer le territoire
          </button>
        )}
        {partner.activation_status === "ACTIF" && (
          <button
            type="button"
            data-testid="admin-suspend-partner"
            onClick={() => runAction("suspendre")}
            disabled={busy}
            className="flex items-center gap-1.5 rounded-full bg-amber-50 px-4 py-2 text-xs font-bold text-amber-700 transition-colors hover:bg-amber-100 disabled:opacity-50"
          >
            <Ban className="h-3.5 w-3.5" /> Suspendre
          </button>
        )}
        {partner.activation_status === "SUSPENDU" && (
          <button
            type="button"
            data-testid="admin-reactivate-partner"
            onClick={() => runAction("reactiver")}
            disabled={busy}
            className="flex items-center gap-1.5 rounded-full bg-sage/20 px-4 py-2 text-xs font-bold text-navy transition-colors hover:bg-sage/30 disabled:opacity-50"
          >
            <PlayCircle className="h-3.5 w-3.5" /> Réactiver
          </button>
        )}
        {["ACTIF", "SUSPENDU"].includes(partner.activation_status) && (
          <button
            type="button"
            data-testid="admin-resiliate-partner"
            onClick={() => runAction("resilier")}
            disabled={busy}
            className="flex items-center gap-1.5 rounded-full bg-red-50 px-4 py-2 text-xs font-bold text-red-500 transition-colors hover:bg-red-100 disabled:opacity-50"
          >
            <XOctagon className="h-3.5 w-3.5" /> Résilier
          </button>
        )}
      </div>
    </div>
  );
};
